import type { FC } from "react";
import { File, X, CheckCircle2 } from "lucide-react";
import { UploadItem, UploadItemStatus } from "./types/upload-item";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

type Props = {
  uploadItem: UploadItem;
};

const FileUploadItem: FC<Props> = ({ uploadItem }) => {
  const { name, status, hasIndexingError, indexingInBackground } = uploadItem;

  const fileName = name.split("/").pop() || name;

  return (
    <div className="group flex items-start gap-2 p-1.5 sm:p-2 rounded-md hover:bg-accent/50 transition-colors">
      <File className="size-4 text-muted-foreground group-hover:text-foreground mt-0.5 shrink-0" />

      <div className="flex-1 min-w-0 space-y-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="text-xs sm:text-sm font-medium truncate cursor-default">
              {fileName}
            </p>
          </TooltipTrigger>
          {fileName.length > 15 && (
            <TooltipContent side="top" className="max-w-[250px]">
              <p className="break-words text-xs">{name}</p>
            </TooltipContent>
          )}
        </Tooltip>

        {status !== UploadItemStatus.Completed &&
          status !== UploadItemStatus.Failed && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground group-hover:text-foreground">
                <span className="truncate">
                  {status === UploadItemStatus.Pending
                    ? "Uploader..."
                    : "Behandler..."}
                </span>
              </div>
            </div>
          )}

        {status === UploadItemStatus.Completed && (
          <p className="text-xs text-success flex items-center gap-1">
            <CheckCircle2 className="size-3 shrink-0" />
            <span className="truncate">
              {indexingInBackground
                ? "Upload fuldført (kildeklargøring startet)"
                : hasIndexingError
                  ? "Upload fuldført (kildeklargøring ikke gennemført)"
                  : "Upload fuldført"}
            </span>
          </p>
        )}

        {status === UploadItemStatus.Failed && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <X className="size-3 shrink-0" />
            <span className="truncate">Upload fejlede</span>
          </p>
        )}
      </div>

      {status !== UploadItemStatus.Completed &&
        status !== UploadItemStatus.Failed && (
          <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0 mt-0.5" />
        )}
      {status === UploadItemStatus.Completed && (
        <CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" />
      )}
      {status === UploadItemStatus.Failed && (
        <div className="size-4 rounded-full bg-destructive flex items-center justify-center shrink-0 mt-0.5">
          <X className="size-3 text-white" />
        </div>
      )}
    </div>
  );
};

export default FileUploadItem;
